import { Link } from "react-router-dom";
import { ScoreBadge } from "./ScoreBadge";
import { SaturationBadge } from "./SaturationBadge";
import type { Opportunity } from "@/lib/mock-data";
import { getScoreTier } from "@/lib/theme";

interface OpportunityCardProps {
  opportunity: Opportunity;
}

export function OpportunityCard({ opportunity }: OpportunityCardProps) {
  const tier = getScoreTier(opportunity.compositeScore);
  const borderColor = tier?.color ?? "rgba(255,255,255,0.08)";
  const displayName = opportunity.opportunityName || opportunity.title;
  const pitch = opportunity.elevatorPitch || opportunity.description;
  const competitors = opportunity.existingCompetitors || [];

  return (
    <Link
      to={`/opportunities/${opportunity.id}`}
      className="block glass-card p-4 card-hover relative overflow-hidden"
      style={{
        borderLeft: `3px solid ${borderColor}`,
        boxShadow: `0 0 20px ${borderColor}10`,
      }}
    >
      <div className="flex items-start justify-between mb-2">
        <SaturationBadge level={opportunity.saturationLevel} size="md" />
        <ScoreBadge score={opportunity.compositeScore} size="md" />
      </div>

      <h3 className="text-foreground font-semibold text-sm leading-tight mb-1">
        {displayName}
      </h3>

      {pitch && (
        <p className="text-muted-foreground text-xs line-clamp-2 mb-2">
          {pitch}
        </p>
      )}

      <div className="flex items-center gap-2 text-[10px] text-muted-foreground uppercase tracking-wider mb-2">
        <span className="bg-[rgba(255,255,255,0.06)] px-1.5 py-0.5 rounded">
          {opportunity.industry}
        </span>
        {opportunity.signalCount && (
          <span className="font-mono">{opportunity.signalCount} signals</span>
        )}
        {opportunity.sourcePlatforms && (
          <span className="font-mono">{opportunity.sourcePlatforms.length} sources</span>
        )}
      </div>

      <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
        {competitors.length > 0 && (
          <span>Competitors: {competitors.length}</span>
        )}
        {opportunity.confidence && (
          <span className="uppercase">Confidence: {opportunity.confidence}</span>
        )}
      </div>

      {/* Mini score bars */}
      <div className="flex gap-3 mt-2">
        <MiniBar label="Pain" value={opportunity.painIntensity} color="#F24B4B" />
        <MiniBar label="Advantage" value={opportunity.distributionAdvantage} color="#44F2D2" />
      </div>
    </Link>
  );
}

function MiniBar({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="flex-1">
      <span className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</span>
      <div className="h-1 bg-[rgba(255,255,255,0.06)] rounded-full mt-0.5 overflow-hidden">
        <div
          className="h-full rounded-full"
          style={{ width: `${value * 10}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
}
